import { cloneDeep } from 'lodash-es';

import { ICountryGroup } from '~/src/dash/api/console-api';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { ICountryState } from '~/src/dash/utils/states';
import { US_DMA_CODES } from '~/src/dash/utils/us-dmas';

export interface IGeo {
  key: string;
  text: string;
}

const _Country = () => (window as any).Country;
const _City = () => (window as any).City;
const _State = () => (window as any).State;

interface ITimeZone {
  zoneName: string;
  gmtOffset: number;
  gmtOffsetName: string;
  abbreviation: string;
  tzName: string;
}

interface ICountry {
  isoCode: string;
  name: string;
  phonecode: string;
  flag: string;
  currency: string;
  latitude: string;
  longitude: string;
  timezones: ITimeZone[];
}

interface IState {
  name: string;
  isoCode: string;
  latitude: string;
  longitude: string;
}

interface ICity {
  name: string;
  isoCode: string;
  latitude: string;
  longitude: string;
}

export const getAllCountries = (): IGeo[] => {
  const allCountries = _Country().getAllCountries();
  const countries = [] as IGeo[];
  allCountries.forEach((country: ICountry) => {
    countries.push({ key: country.isoCode.toLocaleLowerCase(), text: country.name });
  });
  return countries.sort((a: IGeo, b: IGeo) => (a.text < b.text ? 1 : -1));
};

export function generateCountriesMenuList(targetAll?: boolean): IMenuList {
  const countriesList: IMenuList = {};

  getAllCountries().forEach((country: IGeo) => {
    countriesList[country.key] = {
      ID: country.key,
      selected: targetAll ? true : false,
      title: `${fromCountryToFlag(country)} ${country.text}`,
    };
  });

  return countriesList;
}

export function generateDMAsMenu(country: string, selectedDMAs: string[]): IMenuList {
  if (country !== 'us') {
    return {};
  }

  const dmas: IMenuList = US_DMA_CODES.reduce((acc: IMenuList, dma: ICountryState) => {
    acc[dma.key] = {
      ID: dma.key,
      selected: false,
      title: `${dma.key}: ${dma.name}`,
    };
    return acc;
  }, {});

  for (const dma of selectedDMAs) {
    if (dmas[dma]) {
      dmas[dma].selected = true;
    }
  }

  return dmas;
}

export function generateStatesMenuList(country: string, selectedStates: string[]): IMenuList {
  const statesList: IMenuList = {};
  if (country === '') {
    return statesList;
  }

  const statesOfCountry = _State().getStatesOfCountry(country.toUpperCase());
  statesOfCountry.forEach((state: IState) => {
    statesList[state.isoCode] = {
      ID: state.isoCode,
      selected: false,
      title: state.name,
    };
  });

  for (const state of selectedStates) {
    if (statesList[state] !== undefined) {
      statesList[state].selected = true;
    }
  }

  return statesList;
}

export function generateCitiesForStateMenuList(
  country: string,
  state: string,
  selectedCities: string[],
): IMenuList {
  const citiesList: IMenuList = {};
  if (country === '' || state === '') {
    return citiesList;
  }

  let citiesOfState = _City().getCitiesOfState(country.toUpperCase(), state.toUpperCase());

  // Washington, D.C. is recognized as just Washington in places like the Snowflake
  // Also, MaxMind and Snowflake does not recognize other 'cities' listed under DC using
  // this library, such as Chevy Chase, Shaw, etc.
  if (state.toUpperCase() === 'DC') {
    citiesOfState = [
      {
        name: 'Washington',
        countryCode: 'US',
        stateCode: 'DC',
        latitude: '38.89511000',
        longitude: '-77.03637000',
      },
    ];
  }

  citiesOfState.forEach((city: ICity) => {
    // This is needed due to some cities' name containing a comma,
    // which disrupt our code that split and join an array using comma.

    if (city.name.indexOf(',') === -1) {
      citiesList[city.name] = {
        ID: city.name,
        selected: false,
        title: city.name,
      };
    }
  });

  for (const city of selectedCities) {
    if (citiesList[city] !== undefined) {
      citiesList[city].selected = true;
    }
  }

  return citiesList;
}

export function generateCitiesForCountryMenuList(
  country: string,
  selectedCities: string[],
): IMenuList {
  const citiesList: IMenuList = {};
  if (country === '') {
    return citiesList;
  }
  const citiesOfCountry = _City().getCitiesOfCountry(country.toUpperCase());
  if (citiesOfCountry) {
    citiesOfCountry.forEach((city: ICity) => {
      citiesList[city.name] = {
        ID: city.name,
        selected: false,
        title: city.name,
      };
    });
  }

  for (const city of selectedCities) {
    if (citiesList[city] !== undefined) {
      citiesList[city].selected = true;
    }
  }

  return citiesList;
}

export function genCountriesMenuListFromSelectedAndDisable(
  countryIsos: string,
  disabledIDs?: string[],
) {
  const countriesMenuList = generateCountriesMenuList();

  for (const selected of countryIsos.split(',')) {
    const countryIso = selected.toLowerCase();
    if (countriesMenuList[countryIso]) {
      countriesMenuList[countryIso].selected = true;
    }
  }

  // disable for Include fields
  if ((disabledIDs || []).length) {
    for (const disabled of disabledIDs || []) {
      const countryIso = disabled.toLowerCase();
      if (countriesMenuList[countryIso]) {
        countriesMenuList[countryIso].disabled = true;
      }
    }
  }
  return countriesMenuList;
}

// generateCountryMenuListFromIsos creates a menu list from the provided country isos
export function generateCountryMenuListFromIsos(countryIsos: string[]): IMenuList {
  const allCountriesList = generateCountriesMenuList();

  for (const countryIso of countryIsos) {
    if (allCountriesList[countryIso.toLowerCase()]) {
      allCountriesList[countryIso.toLowerCase()].selected = true;
    }
  }

  return allCountriesList;
}

// generateCountryGroupMenuList creates a default menu list from the provided country groups
export function generateCountryGroupMenuList(
  countryGroups: ICountryGroup[],
  selectedCountryGroupIDs: string[] = [],
  selectAllCountryGroups: boolean = false,
): IMenuList {
  const countryGroupList: IMenuList = {};

  for (const countryGroup of countryGroups) {
    const countryGroupSize = countryGroup.CountryIsos.split(',').length;
    const countryLabel = countryGroupSize > 1 ? 'countries' : 'country';
    countryGroupList[countryGroup.ID] = {
      ID: countryGroup.ID.toString(),
      selected: selectAllCountryGroups
        ? true
        : selectedCountryGroupIDs.includes(countryGroup.ID.toString()),
      title: `${countryGroup.Name} (${countryGroupSize} ${countryLabel})`,
    };
  }
  return countryGroupList;
}

export function fromCountryToFlag(country: IGeo): string {
  if (country.key === undefined) {
    return '';
  }
  if (country.key === 'unknown') {
    return String.fromCodePoint(0x1f6ab); // no_entry_sign emoji https://emojipedia.org/prohibited/
  }
  return country.key
    .toUpperCase()
    .replace(/./g, (char: string) => String.fromCodePoint(char.charCodeAt(0) + 127397));
}

// Set the selected field for countries
export function selectCountries(countriesList: IMenuList, selectedCountries: string[]): IMenuList {
  // Sets countries for existing report
  Object.values(countriesList).forEach((listItem: IListItem) => {
    if (selectedCountries.includes(listItem.ID)) {
      countriesList[listItem.ID].selected = true;
    }
  });

  return countriesList;
}

// generateNewTargetingCountryGroupList returns a new menulist based on the input state
// targetingCountryGroupList, adding missing country groups and updating existing ones
export function generateNewTargetingCountryGroupList(
  targetingCountryGroupList: IMenuList,
  countryGroups: ICountryGroup[],
): IMenuList {
  const clonedTargetingCountryGroupList = cloneDeep(targetingCountryGroupList);
  for (const countryGroup of countryGroups) {
    if (clonedTargetingCountryGroupList[countryGroup.ID] === undefined) {
      clonedTargetingCountryGroupList[countryGroup.ID] = {
        ID: countryGroup.ID.toString(),
        selected: false,
        title: countryGroup.Name,
      } as IListItem;
    } else {
      clonedTargetingCountryGroupList[countryGroup.ID].title = countryGroup.Name;
    }
  }
  return clonedTargetingCountryGroupList;
}

export function genTargetingCountryGroupListFromSelectedAndDisable(
  publisherCountryGroups: ICountryGroup[],
  selectedCountryGroupIDs: string,
  disabledIDs?: string[],
) {
  const newCountryGroupList = generateCountryGroupMenuList(publisherCountryGroups);
  const selectedCGIDsList = !selectedCountryGroupIDs.length
    ? []
    : selectedCountryGroupIDs.split(',');
  for (let i = 0; i < selectedCGIDsList.length; i++) {
    const countryGroupID = selectedCGIDsList[i].toString();
    if (newCountryGroupList[countryGroupID]) {
      newCountryGroupList[countryGroupID].selected = true;
    }
  }

  // disable for Include fields
  if ((disabledIDs || []).length) {
    for (const countryGroupID of disabledIDs || []) {
      if (newCountryGroupList[countryGroupID]) {
        newCountryGroupList[countryGroupID].disabled = true;
      }
    }
  }
  return newCountryGroupList;
}
